import { Radio } from "@jugl-web/ui-components/cross-platform/Radio";
import { Switch } from "@jugl-web/ui-components/cross-platform/Switch";
import { FormGroup } from "@jugl-web/ui-components/cross-platform/forms/FormGroup";
import { FormSection } from "@jugl-web/ui-kit/forms/FormSection";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { ManageBoardFormValues, taskObjectNamingPresets } from "../../types";
import { ReactComponent as TitleIcon } from "./assets/gears.svg";
import { CustomNameAlert } from "./components/CustomNameAlert/CustomNameAlert";
import { useBoardSettingsHints } from "../../hooks/useBoardSettingsHints";

export const BoardSettingsSection = ({
  form,
  isFormReadonly,
}: {
  form: UseFormReturn<ManageBoardFormValues>;
  isFormReadonly: boolean;
}) => {
  const { watch, setValue } = form;
  const params = watch();

  const boardSettingsHints = useBoardSettingsHints();

  const { isMobile } = useAppVariant();
  const { t } = useTranslations();

  const selectCustomNaming = useCallback(() => {
    setValue("taskObjectNamingId", "custom", {
      shouldDirty: true,
    });
  }, [setValue]);

  /* not in this release
    const guestSharingOptionsObject: {
    id: GuestSharingOption;
    label: string;
  }[] = useMemo(() => {
    return [
      {
        id: "showTaskDetails",
        label: t({
          id: "tasks-board-form.section.guest-sharing.show-task-details",
          defaultMessage: "Show Task details, including custom fields",
        }),
      },
      {
        id: "showSubtaskDetails",
        label: t({
          id: "tasks-board-form.section.guest-sharing.show-subtasks-details",
          defaultMessage: "Show Subtasks details, including custom fields",
        }),
      },
      {
        id: "showTaskAttachments",
        label: t({
          id: "tasks-board-form.section.guest-sharing.show-task-attachments",
          defaultMessage: "Show Task Attachments",
        }),
      },
      {
        id: "showSubtaskAttachments",
        label: t({
          id: "tasks-board-form.section.guest-sharing.show-subtasks-attachments",
          defaultMessage: "Show Subtasks Attachments",
        }),
      },
      {
        id: "showInvoiceDocuments",
        label: t({
          id: "tasks-board-form.section.guest-sharing.show-invoice-documents",
          defaultMessage: "Show Invoice Documents",
        }),
      },
    ];
  }, [t]);
  */

  return (
    <FormSection
      title={t({
        id: "common.additional",
        defaultMessage: "Additional",
      })}
      icon={<TitleIcon />}
    >
      <div className="flex items-center">
        <Switch
          id="includeOrderDetails"
          isChecked={Boolean(params.includeOrderDetails)}
          isDisabled={isFormReadonly}
          onChange={() =>
            setValue("includeOrderDetails", !params.includeOrderDetails, {
              shouldDirty: true,
            })
          }
        />
        <FormGroup
          labelFor="includeOrderDetails"
          label={t({
            id: "tasks-board-form.section.include-order-details.label",
            defaultMessage: "Include “Order Details” module in tasks",
          })}
          tooltip={
            isMobile ? undefined : boardSettingsHints.includeItemsServicesHint
          }
          classNames={{
            labelClassName: cx(
              "border-none text-dark",
              !isMobile && "group-hover:underline"
            ),
          }}
        />
      </div>

      <div className="flex items-center py-8">
        <Switch
          id="includeBillingFiles"
          isChecked={params.includeBillingFiles}
          isDisabled={isFormReadonly}
          onChange={() =>
            setValue("includeBillingFiles", !params.includeBillingFiles, {
              shouldDirty: true,
            })
          }
        />
        <FormGroup
          labelFor="includeBillingFiles"
          label={t({
            id: "tasks-board-form.section.include-billing-files.label",
            defaultMessage: "Include “Billing Files” module in tasks",
          })}
          tooltip={
            isMobile ? undefined : boardSettingsHints.includeBillingFilesHint
          }
          classNames={{
            labelClassName: cx(
              "border-none text-dark",
              !isMobile && "group-hover:underline"
            ),
          }}
        />
      </div>

      <FormGroup
        label={t({
          id: "tasks-board-form.section.board-settings-how-to-name-items.label",
          defaultMessage: "How to name items in the Board?",
        })}
        tooltip={isMobile ? undefined : boardSettingsHints.taskObjectNamingHint}
        classNames={{
          labelClassName: cx(
            "border-none text-dark",
            !isMobile && "group-hover:underline"
          ),
        }}
      >
        <div className="flex flex-wrap gap-y-1.5 px-10">
          {taskObjectNamingPresets.map((preset) => (
            <label
              className="flex h-10 basis-1/2 cursor-pointer items-center gap-2 font-secondary text-dark select-none"
              key={preset.id}
              onClick={() => {
                if (isFormReadonly) return;
                setValue("taskObjectNamingId", preset.id, {
                  shouldDirty: true,
                });
              }}
            >
              <Radio
                readOnly
                isDisabled={isFormReadonly}
                isChecked={params.taskObjectNamingId === preset.id}
              />
              {preset.plural}
            </label>
          ))}
          <CustomNameAlert
            isFormReadonly={isFormReadonly}
            params={params}
            onSave={({
              singular,
              plural,
            }: {
              singular: string;
              plural: string;
            }) => {
              setValue(
                "customTaskObjectNaming",
                { singular, plural },
                {
                  shouldDirty: true,
                }
              );
              selectCustomNaming();
            }}
            onRadioClickWhenValueIsSet={() => {
              selectCustomNaming();
            }}
          />
        </div>
      </FormGroup>

      {/* not in this release
       <div className="flex pt-8">
        <Switch
          isChecked={Boolean(params.enableGuestSharing)}
          className="mt-[3px]"
          isDisabled={isFormReadonly}
          onChange={() => {
            setValue("enableGuestSharing", !params.enableGuestSharing);
          }}
        />
        <FormGroup
          label={t({
            id: "tasks-board-form.section.guest-task-sharing-options.label",
            defaultMessage: "Allow Tasks sharing through Guest Link",
          })}
          tooltip={
            <>
              <div>
                {t({
                  id: "tasks-board-form.section.guest-task-sharing-options.tooltip",
                  defaultMessage:
                    "Define if tasks in this board can be shared with Guest link",
                })}
              </div>
              <br />
              <div>
                {t({
                  id: "tasks-board-form.section.guest-task-sharing-options.tooltip-2",
                  defaultMessage:
                    "Based on the OTP Settings, Guest link can be opened without any visitor authentication, or only with OTP (One Time Password).",
                })}
              </div>
            </>
          }
          classNames={{
            labelClassName: "border-none group-hover:underline text-dark",
          }}
        >
          <Transition
            as={Fragment}
            show={params.enableGuestSharing}
            enter="transition-opacity duration-200 ease-in delay-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-200 ease-out"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="flex flex-col gap-4 px-3 pt-2 transition-all">
              {guestSharingOptionsObject.map((option) => (
                <Checkbox
                  readOnly
                  isDisabled={isFormReadonly}
                  isChecked={Boolean(params?.guestSharingOptions?.[option.id])}
                  onClick={() => {
                    if (isFormReadonly) return;
                    setValue(
                      `guestSharingOptions.${option.id}`,
                      !params?.guestSharingOptions?.[option.id]
                    );
                  }}
                  label={option.label}
                  labelClassName="font-secondary text-dark text-sm"
                />
              ))}
            </div>
          </Transition>
        </FormGroup>
      </div> */}
    </FormSection>
  );
};
